/* eslint-disable no-param-reassign */
/* eslint-disable class-methods-use-this */
/**
 * Cart Milestones
 * Manages cart value milestone tracking, progress display, and celebrations
 */

class CartMilestones {
  constructor(slideCartInstance) {
    this.slideCart = slideCartInstance;
    this.settings = window.theme?.cart?.milestones || {};
    this.translations = window.theme?.translations?.milestones || {};

    if (!this.settings.enabled) {
      console.log('Cart milestones disabled in theme settings');
      return;
    }

    this.tiers = this.settings.tiers || [];
    if (this.tiers.length === 0) {
      console.warn('No milestone tiers configured');
      return;
    }

    // State
    this.currentTier = 0;
    this.previousTier = 0;
    this.celebratedMilestones = [];
    this.appliedDiscountCode = null;
    this.confetti = null;

    // DOM elements
    this.tracker = null;
    this.progressFill = null;
    this.progressMessage = null;
    this.markersContainer = null;
    this.celebrationOverlay = null;

    this.init();
  }

  /**
   * Initialize milestone tracker
   */
  async init() {
    this.loadFromSession();
    this.cacheDOMElements();
    await this.filterAvailableGifts();
    this.renderMilestoneMarkers();
    this.attachEventListeners();
  }

  /**
   * Cache DOM element references
   */
  cacheDOMElements() {
    this.tracker = document.querySelector('[data-milestone-tracker]');
    this.progressFill = document.querySelector('[data-progress-fill]');
    this.progressMessage = document.querySelector('[data-progress-message]');
    this.markersContainer = document.querySelector('[data-milestone-markers]');
    this.celebrationOverlay = document.querySelector(
      '[data-celebration-overlay]'
    );
  }

  /**
   * Filter out gift milestones with sold-out products
   */
  async filterAvailableGifts() {
    const availabilityChecks = this.tiers.map(async (tier, index) => {
      if (tier.type === 'gift' && tier.giftProduct) {
        // For products with multiple variants or Combined Listings,
        // we'll check availability when showing the selector
        if (
          !tier.giftProduct.hasOnlyDefaultVariant ||
          tier.giftProduct.variantsCount > 1
        ) {
          // Assume available - will be checked when selector is shown
          return { index, available: true };
        }

        // Single variant - check availability
        const isAvailable = await this.checkGiftAvailability(
          tier.giftProduct.defaultVariantId
        );
        return { index, available: isAvailable };
      }
      return { index, available: true };
    });

    const results = await Promise.all(availabilityChecks);

    // Filter out unavailable gift milestones
    this.tiers = this.tiers.filter((_, index) => {
      const result = results.find(r => r.index === index);
      return result.available;
    });
  }

  /**
   * Check if a gift variant is available for purchase
   */
  async checkGiftAvailability(variantId) {
    if (!variantId) return false;

    try {
      // Use Shopify Cart API to check if variant can be added
      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 0, // Quantity 0 to just check availability
            },
          ],
        }),
      });

      // If we get a 422 with "sold out" message, product is unavailable
      if (response.status === 422) {
        const errorData = await response.json();
        const errorMessage = errorData.description || errorData.message || '';

        if (
          errorMessage.toLowerCase().includes('ausverkauft') ||
          errorMessage.toLowerCase().includes('sold out')
        ) {
          return false;
        }
      }

      // For quantity 0, we expect it to potentially fail, but if variant exists
      // and is not sold out, it's available
      return true;
    } catch (error) {
      // On error, assume available to not hide milestone unnecessarily
      return true;
    }
  }

  /**
   * Attach event listeners
   */
  attachEventListeners() {
    // Listen for cart updates
    document.addEventListener('cart:updated', () => {
      if (this.slideCart?.cartData) {
        this.updateProgress(this.slideCart.cartData);
      }
    });

    // Close celebration on Escape key
    document.addEventListener('keydown', e => {
      if (
        e.key === 'Escape' &&
        this.celebrationOverlay &&
        !this.celebrationOverlay.hidden
      ) {
        this.closeCelebration();
      }
    });
  }

  /**
   * Render milestone markers
   */
  renderMilestoneMarkers() {
    if (!this.markersContainer) return;

    this.markersContainer.innerHTML = '';

    // Get the last milestone to calculate positions
    const lastMilestone = this.tiers[this.tiers.length - 1];
    if (!lastMilestone) return;

    this.tiers.forEach((tier, index) => {
      const marker = this.createMilestoneMarker(
        tier,
        index,
        lastMilestone.amount
      );
      this.markersContainer.appendChild(marker);
    });
  }

  /**
   * Create a milestone marker element
   */
  createMilestoneMarker(tier, index, lastMilestoneAmount) {
    const marker = document.createElement('div');

    // Calculate position as percentage of the last milestone
    const positionPercent = (tier.amount / lastMilestoneAmount) * 100;

    marker.className = 'absolute transition-all duration-300';
    marker.style.left = `${positionPercent}%`;
    marker.style.transform = 'translateX(-100%) translateY(-50%)'; // Center the marker on the position
    marker.setAttribute('data-milestone-item', index + 1);
    marker.setAttribute('data-amount', tier.amount);

    const formattedAmount = this.formatMoney(tier.amount);
    const translatedLabel = this.getTranslatedLabel(tier);
    marker.setAttribute('aria-label', `${translatedLabel} — ${formattedAmount}`);

    // Icon container
    const iconContainer = document.createElement('div');
    iconContainer.className =
      'w-8 h-8 rounded-full bg-white border-2 border-gray-300 flex items-center justify-center transition-all duration-300 opacity-50';
    iconContainer.setAttribute('data-milestone-icon', '');

    const icon = document.createElement('div');
    icon.className = 'w-5 h-5 text-gray-400 transition-colors duration-300';
    icon.innerHTML = this.getIconHTML(tier.type);
    iconContainer.appendChild(icon);

    marker.appendChild(iconContainer);

    return marker;
  }

  /**
   * Get translated label for a milestone tier based on its type
   */
  getTranslatedLabel(tier) {
    switch (tier.type) {
      case 'free_shipping':
        return this.translations.freeShippingShort || tier.label;
      case 'discount':
        return this.translations.discountShort || tier.label;
      case 'gift':
        return this.translations.freeGift || tier.label;
      default:
        return tier.label;
    }
  }

  /**
   * Get icon HTML for milestone type
   * All icons use "fill" set to the CSS variable for the gurke color
   */
  getIconHTML(type) {
    const gurkeFill = 'style="color: #255a2e; fill: currentColor;"';
    const icons = {
      free_shipping: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" ${gurkeFill} class="w-full h-full"><path d="M3.375 4.5C2.339 4.5 1.5 5.34 1.5 6.375V13.5h12V6.375c0-1.036-.84-1.875-1.875-1.875h-8.25zM13.5 15h-12v2.625c0 1.035.84 1.875 1.875 1.875h.375a3 3 0 116 0h3a.75.75 0 00.75-.75V15z"/><path d="M8.25 19.5a1.5 1.5 0 10-3 0 1.5 1.5 0 003 0zM15.75 6.75a.75.75 0 00-.75.75v11.25c0 .087.015.17.042.248a3 3 0 015.958.464c.853-.175 1.522-.935 1.464-1.883a18.659 18.659 0 00-3.732-10.104 1.837 1.837 0 00-1.47-.725H15.75z"/><path d="M19.5 19.5a1.5 1.5 0 10-3 0 1.5 1.5 0 003 0z"/></svg>`,
      discount: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" ${gurkeFill} class="w-full h-full"><path fill-rule="evenodd" d="M5.25 2.25a3 3 0 00-3 3v4.318a3 3 0 00.879 2.121l9.58 9.581c.92.92 2.39 1.186 3.548.428a18.849 18.849 0 005.441-5.44c.758-1.16.492-2.629-.428-3.548l-9.58-9.581a3 3 0 00-2.122-.879H5.25zM6.375 7.5a1.125 1.125 0 100-2.25 1.125 1.125 0 000 2.25z" clip-rule="evenodd"/></svg>`,
      gift: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" ${gurkeFill} class="w-full h-full"><path d="M9.375 3a1.875 1.875 0 000 3.75h1.875v4.5H3.375A1.875 1.875 0 011.5 9.375v-.75c0-1.036.84-1.875 1.875-1.875h3.193A3.375 3.375 0 0112 2.753a3.375 3.375 0 015.432 3.997h3.943c1.035 0 1.875.84 1.875 1.875v.75c0 1.036-.84 1.875-1.875 1.875H12.75v-4.5h1.875a1.875 1.875 0 10-1.875-1.875V6.75h-1.5V4.875C11.25 3.839 10.41 3 9.375 3zM11.25 12.75H3v6.75a2.25 2.25 0 002.25 2.25h6v-9zM12.75 12.75v9h6.75a2.25 2.25 0 002.25-2.25v-6.75h-9z"/></svg>`,
    };
    return icons[type] || icons.free_shipping;
  }

  /**
   * Update progress based on cart data
   */
  updateProgress(cartData) {
    if (!cartData || !this.progressFill || !this.tracker) return;

    const cartTotal = cartData.total_price || 0;
    this.previousTier = this.currentTier;
    this.currentTier = this.getCurrentTier(cartTotal);

    // Find next milestone and last milestone
    const nextMilestone = this.getNextMilestone(cartTotal);
    const lastMilestone = this.tiers[this.tiers.length - 1];

    // Update progress bar - progress is based on the LAST milestone
    if (lastMilestone) {
      // Calculate progress from 0 to last milestone amount
      const progress = this.calculateProgress(cartTotal, lastMilestone.amount);
      this.progressFill.style.width = `${progress}%`;
      this.progressFill.setAttribute('aria-valuenow', Math.round(progress));

      // Update message based on next milestone
      if (nextMilestone) {
        const remaining = this.formatMoney(nextMilestone.amount - cartTotal);
        const translatedLabel = this.getTranslatedLabel(nextMilestone);
        const message = this.translations.progressText
          ?.replace('{{amount}}', remaining)
          ?.replace('{{reward}}', translatedLabel);
        if (this.progressMessage && message) {
          this.progressMessage.textContent = message;
        }
      } else if (this.progressMessage) {
        // All milestones completed
        this.progressMessage.textContent = this.translations.completed;
      }
    }

    // Update marker states
    this.updateMilestoneStates(cartTotal);

    // Check if we reached a new milestone
    if (this.currentTier > this.previousTier) {
      const reachedMilestone = this.tiers[this.currentTier - 1];
      if (
        reachedMilestone &&
        !this.celebratedMilestones.includes(this.currentTier)
      ) {
        this.celebrateMilestone(reachedMilestone, this.currentTier);
      }
    }

    // Check if cart dropped below gift milestone threshold
    if (this.currentTier < this.previousTier) {
      this.handleCartDropBelowMilestone(cartTotal);
    }

    // Check if gift was removed but cart still qualifies for gift milestone
    this.checkForMissingGift(cartTotal);

    // Save to session
    this.saveToSession();

    // Emit progress update event
    document.dispatchEvent(
      new CustomEvent('milestone:progress-updated', {
        detail: { cartTotal, currentTier: this.currentTier, nextMilestone },
      })
    );
  }

  /**
   * Update visual states of milestone markers
   */
  updateMilestoneStates(cartTotal) {
    this.tiers.forEach((tier, index) => {
      const marker = document.querySelector(
        `[data-milestone-item="${index + 1}"]`
      );
      if (!marker) return;

      const iconContainer = marker.querySelector('[data-milestone-icon]');
      const icon = iconContainer?.querySelector('div');

      if (cartTotal >= tier.amount) {
        // Completed state
        iconContainer?.classList.remove('border-gray-300', 'opacity-50');
        iconContainer?.classList.add('border-gurke', 'bg-gurke', 'opacity-100');
        icon?.classList.remove('text-gray-400');
        icon?.classList.add('text-white');
      } else if (this.getNextMilestone(cartTotal)?.amount === tier.amount) {
        // Active (next) state
        iconContainer?.classList.remove(
          'border-gray-300',
          'bg-gurke',
          'opacity-50'
        );
        iconContainer?.classList.add(
          'border-gurke',
          'opacity-100',
          'scale-110'
        );
        icon?.classList.remove('text-gray-400', 'text-white');
        icon?.classList.add('text-gurke');
      } else {
        // Inactive state
        iconContainer?.classList.remove(
          'border-gurke',
          'bg-gurke',
          'opacity-100',
          'scale-110'
        );
        iconContainer?.classList.add('border-gray-300', 'opacity-50');
        icon?.classList.remove('text-gurke', 'text-white');
        icon?.classList.add('text-gray-400');
      }
    });
  }

  /**
   * Celebrate milestone achievement
   */
  celebrateMilestone(milestone, tierIndex) {
    // Mark as celebrated
    this.celebratedMilestones.push(tierIndex);
    this.saveToSession();

    // Apply reward
    this.applyReward(milestone);

    // Show celebration if enabled
    if (this.settings.showCelebrations) {
      this.showCelebration(milestone);
    }

    // Emit event
    document.dispatchEvent(
      new CustomEvent('milestone:reached', {
        detail: { milestone, tier: tierIndex },
      })
    );
  }

  /**
   * Apply milestone reward
   */
  applyReward(milestone) {
    switch (milestone.type) {
      case 'discount':
        if (milestone.discountCode) {
          this.applyDiscountCode(milestone.discountCode);
        }
        break;
      case 'gift':
        if (milestone.giftProduct) {
          // Check if product has multiple variants (Combined Listing or multi-variant)
          if (
            !milestone.giftProduct.hasOnlyDefaultVariant ||
            milestone.giftProduct.variantsCount > 1
          ) {
            // Show gift selector UI for user to choose
            this.showGiftSelector(milestone);
          } else {
            // Single variant - auto add like before
            this.addGiftToCart(milestone.giftProduct.defaultVariantId);
          }
        }
        break;
      case 'free_shipping':
        // Free shipping is typically handled by Shopify shipping rates
        break;
      default:
        break;
    }
  }

  /**
   * Show the gift selector UI
   */
  async showGiftSelector(milestone) {
    // Initialize GiftSelector if not already done
    if (!this.giftSelector && window.GiftSelector) {
      this.giftSelector = new window.GiftSelector(this);
    }

    if (this.giftSelector) {
      await this.giftSelector.init(milestone);
    }
  }

  /**
   * Hide the gift selector UI
   */
  hideGiftSelector() {
    if (this.giftSelector) {
      this.giftSelector.collapse();
    }
  }

  /**
   * Remove gift from cart (when cart drops below threshold)
   */
  async removeGiftFromCart() {
    const cartData = this.slideCart?.cartData;
    if (!cartData) return;

    const giftItemIndex = cartData.items.findIndex(
      item => item.properties && item.properties._gift_item === 'true'
    );

    if (giftItemIndex === -1) return;

    const line = giftItemIndex + 1;

    try {
      const changeUrl = window.routes?.cart_change_url || '/cart/change';
      await fetch(`${changeUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity: 0 }),
      });

      // Refresh cart data
      const cartUrl = window.routes?.cart_url || '/cart';
      const cartResponse = await fetch(`${cartUrl}.js`);
      const newCartData = await cartResponse.json();

      if (this.slideCart) {
        this.slideCart.cartData = newCartData;
        this.slideCart.renderCart();
        this.slideCart.updateHeaderCount();
      }
    } catch (error) {
      // Silent fail - gift removal is not critical
    }
  }

  /**
   * Handle when cart drops below a milestone threshold
   */
  async handleCartDropBelowMilestone(cartTotal) {
    // Find if there was a gift milestone that we no longer qualify for
    const giftMilestone = this.tiers.find(
      tier =>
        tier.type === 'gift' && tier.giftProduct && cartTotal < tier.amount
    );

    if (giftMilestone) {
      // Remove the gift from cart
      await this.removeGiftFromCart();

      // Hide the gift selector
      this.hideGiftSelector();

      // Reset gift selector state
      if (this.giftSelector) {
        this.giftSelector.reset();
      }
    }
  }

  /**
   * Check if a gift should be in cart but isn't (user removed it)
   */
  checkForMissingGift(cartTotal) {
    // Find gift milestones the cart qualifies for
    const qualifyingGiftMilestone = this.tiers.find(
      tier =>
        tier.type === 'gift' && tier.giftProduct && cartTotal >= tier.amount
    );

    if (!qualifyingGiftMilestone) return;

    // Check if there's already a gift in the cart
    const cartData = this.slideCart?.cartData;
    const hasGiftInCart = cartData?.items.some(
      item => item.properties && item.properties._gift_item === 'true'
    );

    // If no gift in cart but qualifying, show selector
    if (!hasGiftInCart) {
      // For multi-variant products, show selector
      if (
        !qualifyingGiftMilestone.giftProduct.hasOnlyDefaultVariant ||
        qualifyingGiftMilestone.giftProduct.variantsCount > 1
      ) {
        this.showGiftSelector(qualifyingGiftMilestone);
      }
    }
  }

  /**
   * Apply discount code to checkout
   */
  applyDiscountCode(code) {
    this.appliedDiscountCode = code;
    this.saveToSession();

    // Update all checkout links and forms
    const checkoutForms = document.querySelectorAll('[action="/checkout"]');
    checkoutForms.forEach(form => {
      form.action = `/checkout?discount=${encodeURIComponent(code)}`;
    });

    const checkoutButtons = document.querySelectorAll('a[href*="/checkout"]');
    checkoutButtons.forEach(button => {
      const currentHref = button.getAttribute('href') || '/checkout';
      const baseUrl = currentHref.split('?')[0];
      button.setAttribute(
        'href',
        `${baseUrl}?discount=${encodeURIComponent(code)}`
      );
    });

    document.dispatchEvent(
      new CustomEvent('milestone:discount-applied', {
        detail: { code },
      })
    );
  }

  /**
   * Add gift product to cart automatically
   */
  async addGiftToCart(variantId) {
    if (!variantId) return;

    try {
      // Add gift product to cart with special property
      const formData = {
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: {
              _gift_item: 'true',
            },
          },
        ],
      };

      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        // Exit silently - gift won't be added
        return;
      }

      // Success - parse the cart data
      // Cart API should return JSON even if content-type header is missing
      const result = await response.json();

      // Update slideCart's cartData directly
      if (this.slideCart) {
        this.slideCart.cartData = result;
        this.slideCart.renderCart();
        this.slideCart.updateHeaderCount();
      }

      // Dispatch event to notify that gift was added
      document.dispatchEvent(
        new CustomEvent('milestone:gift-added', {
          detail: { variantId, items: result.items },
        })
      );

      // Dispatch cart:updated event for milestone progress update
      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: result,
        })
      );
    } catch (error) {
      // Silently fail - gift won't be added
    }
  }

  /**
   * Show celebration overlay
   */
  showCelebration(milestone) {
    if (!this.celebrationOverlay) return;

    const messageEl = this.celebrationOverlay.querySelector(
      '[data-celebration-message]'
    );
    const subtitleEl = this.celebrationOverlay.querySelector(
      '[data-celebration-subtitle]'
    );

    if (subtitleEl) {
      const translatedLabel = this.getTranslatedLabel(milestone);
      const message = this.translations.celebrationMessage?.replace(
        '{{reward}}',
        translatedLabel
      );
      subtitleEl.textContent = message || `${translatedLabel} freigeschaltet!`;
    }

    // Show overlay
    this.celebrationOverlay.hidden = false;
    this.celebrationOverlay.classList.remove('opacity-0');
    this.celebrationOverlay.classList.add('opacity-100');

    // Scale in message
    if (messageEl) {
      setTimeout(() => {
        messageEl.classList.remove('scale-0');
        messageEl.classList.add('scale-100');
      }, 100);
    }

    // Start confetti
    if (typeof window.ConfettiAnimation !== 'undefined') {
      this.confetti = new window.ConfettiAnimation('confetti-canvas', {
        colors: ['#255a2e', '#80a758', '#d9e6a1', '#e83a43'],
      });
      this.confetti.init();
    }

    // Auto-close after 3 seconds
    setTimeout(() => {
      this.closeCelebration();
    }, 3000);
  }

  /**
   * Close celebration overlay
   */
  closeCelebration() {
    if (!this.celebrationOverlay) return;

    const messageEl = this.celebrationOverlay.querySelector(
      '[data-celebration-message]'
    );

    // Scale out message
    if (messageEl) {
      messageEl.classList.remove('scale-100');
      messageEl.classList.add('scale-0');
    }

    // Fade out overlay
    this.celebrationOverlay.classList.remove('opacity-100');
    this.celebrationOverlay.classList.add('opacity-0');

    setTimeout(() => {
      this.celebrationOverlay.hidden = true;
    }, 300);

    // Stop confetti
    if (this.confetti) {
      this.confetti.stop();
      this.confetti = null;
    }
  }

  /**
   * Calculate progress percentage (legacy method, kept for compatibility)
   */
  calculateProgress(cartTotal, milestoneAmount) {
    if (milestoneAmount === 0) return 0;
    return Math.min((cartTotal / milestoneAmount) * 100, 100);
  }

  /**
   * Calculate progress percentage between two milestones
   * @param {number} cartTotal - Current cart total in cents
   * @param {number} previousAmount - Previous milestone amount (0 if first)
   * @param {number} nextAmount - Next milestone amount
   * @returns {number} Progress percentage (0-100)
   */
  calculateProgressBetweenMilestones(cartTotal, previousAmount, nextAmount) {
    // If cart hasn't reached previous milestone, return 0
    if (cartTotal < previousAmount) return 0;

    // Calculate the range between milestones
    const range = nextAmount - previousAmount;
    if (range === 0) return 100;

    // Calculate progress within this range
    const progressInRange = cartTotal - previousAmount;
    const percentage = (progressInRange / range) * 100;

    return Math.min(Math.max(percentage, 0), 100);
  }

  /**
   * Get current tier based on cart total
   */
  getCurrentTier(cartTotal) {
    let tier = 0;
    this.tiers.forEach((milestone, index) => {
      if (cartTotal >= milestone.amount) {
        tier = index + 1;
      }
    });
    return tier;
  }

  /**
   * Get next milestone to reach
   */
  getNextMilestone(cartTotal) {
    return this.tiers.find(m => cartTotal < m.amount) || null;
  }

  /**
   * Format money amount (cents to active market currency)
   */
  formatMoney(cents) {
    const value = (cents / 100).toFixed(2).replace('.', ',');
    const symbol = this.settings.currencySymbol || '€';
    return symbol === '€' ? `${value}€` : `${value} ${symbol}`;
  }

  /**
   * Save state to session storage
   */
  saveToSession() {
    try {
      const state = {
        celebratedMilestones: this.celebratedMilestones,
        appliedDiscountCode: this.appliedDiscountCode,
        lastCartTotal: this.slideCart?.cartData?.total_price || 0,
        lastUpdateTimestamp: Date.now(),
      };
      sessionStorage.setItem('cart_milestones_state', JSON.stringify(state));
    } catch (e) {
      // Failed to save to session storage
    }
  }

  /**
   * Load state from session storage
   */
  loadFromSession() {
    try {
      const saved = sessionStorage.getItem('cart_milestones_state');
      if (saved) {
        const state = JSON.parse(saved);
        this.celebratedMilestones = state.celebratedMilestones || [];
        this.appliedDiscountCode = state.appliedDiscountCode || null;
        this.previousTier = this.getCurrentTier(state.lastCartTotal || 0);
      }
    } catch (e) {
      // Failed to load from session storage
    }
  }
}

// Auto-initialize when DOM is ready and slideCart exists
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartMilestones);
} else {
  initCartMilestones();
}

function initCartMilestones() {
  // Wait for slideCart to be available
  const checkSlideCart = setInterval(() => {
    if (window.slideCart && window.theme?.cart?.milestones?.enabled) {
      clearInterval(checkSlideCart);
      window.cartMilestones = new CartMilestones(window.slideCart);
    }
  }, 100);

  // Timeout after 5 seconds
  setTimeout(() => clearInterval(checkSlideCart), 5000);
}
